import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { generateOfferPageRoom, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { validateSemiForm } from 'lib/forms/formUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { ComponentProps } from 'react'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import OfferCapacityWithAgesSelectBox from './OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelection from './OfferCapacityWithAgesSelection'
import OfferPageFormattedOccupancy from './OfferPageFormattedOccupancy'
import styled from 'styled-components'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { MAX_TRAVELLERS_FOR_MALDIVES_CHARTER } from 'constants/charterFlight'
import { useIntl } from 'react-intl'

const StyledOfferCapacityWithAgesSelectBox = styled(OfferCapacityWithAgesSelectBox)`
  &.elevated {
    position: relative;
    z-index: 3;
  }
`

// NOTE: Would be nice to have this a separate type
type DropdownPlacement = Pick<ComponentProps<typeof DropdownSheet>, 'placement'>

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'boundaryRef' | 'open'> {
  capacities?: Array<App.RoomCapacity>
  occupantUnit?: 'guest' | 'traveller'
  childrenAgeLabel?: string
  onApply?: (rooms: Array<App.RoomOccupants>, keepBrowsing?: boolean) => void
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  disableMultiRoom?: boolean
  className?: string
  iconDisabled?: boolean
  maxRooms?: number
  dropdownPlacement?: DropdownPlacement
  dropdownCtaText?: string
  rooms?: Array<App.RoomOccupants>
  charterBundleRoute?: App.CharterBundleRoute
}

function OfferCapacityWithAgesSelect(props: Props) {
  const intl = useIntl()
  const {
    open,
    capacities,
    occupantUnit = 'guest',
    childrenAgeLabel,
    onApply,
    disableMultiRoom,
    onClick,
    boundaryRef,
    className,
    iconDisabled,
    maxRooms,
    rooms,
    dropdownCtaText,
    charterBundleRoute,
  } = props

  const defaultChildrenAgeLabel = intl.formatMessage({
    id: 'bhFP3GQSne',
    defaultMessage: 'Age at time of check-in',
    description: 'Label for children age selection indicating age at check-in time',
  })
  const trigger = useRef(null)
  const container = useRef<HTMLDivElement>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useScreenSizeOnly('mobile')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const totalTravellers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  // Disable individual +1 buttons when at max (9)
  const maxTravellersReached = totalTravellers >= MAX_TRAVELLERS_FOR_MALDIVES_CHARTER

  const dismissOccupancyFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
    trackEvent('OCCUPANCY_FILTERS_CLOSED')
  }, [dispatch, trackEvent])

  const onApplyClose = useCallback(() => {
    if (validateSemiForm(dropdownRef.current!)) {
      dismissOccupancyFilter()
    }
    // if there's no rooms in state, we must still be using the default
    // so pass that through
    onApply?.(hasSelectedRooms(rooms) ? rooms! : [generateOfferPageRoom()])
  }, [dismissOccupancyFilter, onApply, rooms])

  const isDropdownOpen = open && !isMobile
  const showMaxTravellersWarning = maxTravellersReached && !!charterBundleRoute

  return (
    <div ref={container} className={className}>
      <StyledOfferCapacityWithAgesSelectBox
        ref={trigger}
        className={isDropdownOpen ? 'elevated' : undefined}
        occupantUnit={occupantUnit}
        onClick={onClick}
        multiRoomDisabled={disableMultiRoom}
        iconDisabled={iconDisabled}
      />
      <DropdownSheet
        ref={dropdownRef}
        data-testid="offer-capacity-with-ages-select-dropdown"
        anchorRef={trigger}
        triggerRef={trigger}
        boundaryRef={boundaryRef}
        size="L"
        title={intl.formatMessage({
          id: 'uyUna4DNym',
          defaultMessage: 'Who is going?',
          description: 'Title for occupancy selection dropdown',
        })}
        open={isDropdownOpen}
        onClose={dismissOccupancyFilter}
        placement="bottom-start"
        primaryActionProps={{
          children: dropdownCtaText || intl.formatMessage({
            id: 'kCIzK3xrTf',
            defaultMessage: 'Apply',
            description: 'Button text to apply occupancy selection',
          }),
          onClick: onApplyClose,
        }}
        footerStart={<Group direction="horizontal-reverse" gap={8} verticalAlign="center" horizontalAlign="space-between">
          <BodyText variant="large"><OfferPageFormattedOccupancy /></BodyText>
          {showMaxTravellersWarning && (
            <BodyText variant="small" colour="warning">
              {intl.formatMessage(
                {
                  id: 'dpM5yHNulr',
                  defaultMessage: 'Charter bundle can support a maximum of {maxTravellers} travellers',
                  description: 'Warning message about maximum travellers for charter bundle',
                },
                { maxTravellers: MAX_TRAVELLERS_FOR_MALDIVES_CHARTER },
              )}
            </BodyText>
          )}
        </Group>}
      >
        <OfferCapacityWithAgesSelection
          maxRooms={maxRooms}
          capacities={capacities}
          childrenAgeLabel={childrenAgeLabel ?? defaultChildrenAgeLabel}
          disableMultiRoom={disableMultiRoom}
          disableAddOccupants={maxTravellersReached && !!charterBundleRoute}
          maxOccupants={charterBundleRoute ? MAX_TRAVELLERS_FOR_MALDIVES_CHARTER : undefined}
        />
      </DropdownSheet>
    </div>
  )
}

export default withOfferPageState((state) => ({
  open: state.activeFilter === 'capacity',
  rooms: state.rooms,
  charterBundleRoute: state.charterBundleRoute,
}), OfferCapacityWithAgesSelect)
